import { useContext, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Third-party packages.
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

// Types.
import ChartOption from '../types/instance/ChartOption';
import { FilterType } from '../typescript/Enums';
import FilterOption from '../types/instance/FilterOption';
import { highchartsCredits } from "../helpers/utils";

// Contexts
import { useLicences } from "../context/LicenceContext";
import { TimeRangeContext } from "../context/TimeRangeContext";
import { buildApiQuery } from "../helpers/timeRangeUtils";
import { useIsInStatementSidePanel } from "../context/StatementSidePanelContext";
import ConditionalRender from "../helpers/ConditionalRender";

// Components
import ChatButton from "./chatGPT/ChatButton";
import Prompts from "./chatGPT/Prompts";

// Highchart modules - note, the position of these in this file are important.
require("highcharts/modules/annotations")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);

type PieChartProps = {
    filtersOptions: FilterOption[],
    uniqueIndex: number,
    chartOptions: ChartOption[],
    defaultChartOption: ChartOption,
    setFilterOptions: Function,
    instanceType?: string,
    onSliceClick?: (point: any, chartKey: string) => void,
    title?: string,
    hideOptions?: boolean,
    loading?: boolean,
    customTooltipFormatter?: (point: any) => string
};

function PieChart(props: PieChartProps) {
    const timeRangeContext = useContext(TimeRangeContext)
    const navigate = useNavigate();
    const { licences } = useLicences();
    const isInStatementSidePanel = useIsInStatementSidePanel();

    // Create a unique localStorage key for this pie chart instance
    const storageKey = `pieChart_${props.uniqueIndex}_selectedOption`;

    const [chartOption, setChartOption] = useState<ChartOption>(props.defaultChartOption);

    // Handle loading saved preference and updating chart option
    useEffect(() => {
        try {
            const savedOptionKey = localStorage.getItem(storageKey);
            if (savedOptionKey && props.chartOptions.length > 0) {
                // Check if saved option is still valid with current chartOptions
                const savedOption = props.chartOptions.find(option => option.key === savedOptionKey);
                if (savedOption) {
                    // Use saved option if it exists and has data
                    setChartOption(savedOption);
                    return;
                } else {
                    // Saved option no longer exists, clear it from storage
                    localStorage.removeItem(storageKey);
                }
            }

            // Fall back to default option
            setChartOption(props.defaultChartOption);

        } catch (error) {
            console.warn('Failed to handle chart option update:', error);
            setChartOption(props.defaultChartOption);
        }
    }, [props.defaultChartOption, props.chartOptions, storageKey]);

    function setOption(chosenOption: string) {

        let chartOptions = props.chartOptions.filter(chartOption => chartOption.key === chosenOption);

        if (chartOptions.length > 0) {

            // Requested chart option found.
            setChartOption(chartOptions[0]);

            // Save the selection to localStorage
            try {
                localStorage.setItem(storageKey, chosenOption);
            } catch (error) {
                console.warn('Failed to save chart option to localStorage:', error);
            }
        }
    }

    const chartOptions = useMemo(() => {
        return {
            chart: {
                height: '300px',
                plotBackgroundColor: null,
                plotBorderWidth: null,
                plotShadow: false,
                spacing: [0, 0, 0, 0],
                type: 'pie',
                style: {
                    fontFamily: 'system-ui, Arial, sans-serif',
                    fontSize: '1rem',
                    letterSpacing: 'normal'
                }
            },
            title: { text: '' },
            tooltip: {
                useHTML: true,
                formatter: function (): string {
                    // @ts-ignore
                    const point = this.point;

                    // Use custom tooltip formatter if provided
                    if (props.customTooltipFormatter) {
                        return props.customTooltipFormatter(point);
                    }

                    // Default formatter
                    const title = point.name;
                    const formattedTime = point.formatted;
                    const percentage = point.percentage.toFixed(0);

                    return `
            <div>
                <b>${title}</b>
                <div class="d-flex justify-content-between">
                    <span>Total Time:</span>
                    <b style="padding-left: 20px">${formattedTime}</b>
                </div>
                <div class="d-flex justify-content-between">
                    <span>Time Percentage:</span>
                    <b style="padding-left: 20px">${percentage}%</b>
                </div>
            </div>`;
                }
            },
            accessibility: {
                enabled: false,
                point: {
                    valueSuffix: '%'
                }
            },
            plotOptions: {
                animation: false,
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b>: {point.formatted}'
                    },
                    // Always provide a stable events object to avoid Highcharts runtime errors
                    events: {
                        click: typeof props.onSliceClick === "function" ? () => {} : () => {},
                        mouseOver: () => {},
                        mouseOut: () => {}
                    },
                    point: {
                        events: {
                            click: function (event: any) {
                                // Prevent default handler for non-filterable charts like "Label"
                                if (typeof props.onSliceClick === "function" || chartOption.key === "Label") {
                                    if (typeof props.onSliceClick === "function") {
                                        props.onSliceClick(event.point, chartOption.key);
                                    }
                                    // Do nothing for "Label" chart
                                    return;
                                }

                                // TODO Find a common mechanism for all click events.
                                function shouldRedirect(name: string): boolean {
                                    switch (name) {
                                        case 'Batches':
                                        case 'Statements':
                                        case 'Grouped Batches':
                                        case 'Grouped Statements':
                                            return true;

                                        default:
                                            return false;
                                    }
                                }

                                function getFilterType(name: string): FilterType {
                                    switch (name) {
                                        case 'Batches':
                                            // return FilterType.Batches;
                                            break;

                                        case 'Clients':
                                            return FilterType.Clients;

                                        case 'Schemas':
                                            return FilterType.Databases;

                                        case 'Databases':
                                            return FilterType.Databases;

                                        case 'Programs':
                                            return FilterType.Programs;

                                        case 'Statements':
                                            // return FilterType.Statements;
                                            break;

                                        case 'Sessions':
                                            return FilterType.Sessions;

                                        case 'Users':
                                            return FilterType.Users;

                                        case 'Waits':
                                            return FilterType.Waits;

                                        default:
                                            break;
                                    }

                                    throw new Error
                                    (`No equivalent filter type for ${name}.`);
                                }

                                if (shouldRedirect(chartOption.key)) {
                                    const pathname: string = `${event.point.url}/activity`;
                                    const search: string = event.point.queryParameters ?
                                            event.point.queryParameters :
                                            buildApiQuery(timeRangeContext.timeRange);
                                    navigate({ pathname, search });
                                } else {
                                    const filterType: FilterType = getFilterType(chartOption.key);
                                    // @ts-ignore
                                    let options: FilterOption[] = props.filtersOptions;

                                    // TODO Find out why options becomes empty...
                                    if (options.length <= filterType) {
                                        options =
                                                [
                                                    {
                                                        filterType: FilterType.Waits,
                                                        filters: []
                                                    },
                                                    {
                                                        filterType: FilterType.Databases,
                                                        filters: []
                                                    },
                                                    {
                                                        filterType: FilterType.Sessions,
                                                        filters: []
                                                    },
                                                    {
                                                        filterType: FilterType.Clients,
                                                        filters: []
                                                    },
                                                    {
                                                        filterType: FilterType.Users,
                                                        filters: []
                                                    },
                                                    {
                                                        filterType: FilterType.Programs,
                                                        filters: []
                                                    },
                                                    {
                                                        filterType: FilterType.Modules,
                                                        filters: []
                                                    }
                                                ];
                                    }

                                    const filters: string[] =
                                            options[filterType].filters;

                                    // Only allow one filter of each type
                                    if (filters.length === 0 &&
                                            event.point.name !== '-') {
                                        filters.push(event.point.name);
                                        props.setFilterOptions(options);
                                    }
                                }
                            }
                        }
                    }
                }
            },
            series: [{
                name: JSON.parse(JSON.stringify(chartOption.key)),
                colorByPoint: true,
                innerSize: '50%',
                data: JSON.parse(JSON.stringify(chartOption.data)),
                dataLabels: {
                    style: {
                        fontSize: '11.3px',
                    }
                }
                // No series.events.click here! Only use plotOptions.pie.point.events.click
            }],
            exporting: { enabled: false },
            credits: { enabled: highchartsCredits(licences) }
        };

    }, [chartOption.data, chartOption.key, props.filtersOptions, props.onSliceClick]);


    Highcharts.setOptions({
        plotOptions: { series: { animation: false } },
        lang: {
            thousandsSep: ','
        }
    });

    if (props.chartOptions.length === 0) {

        // No chart options passed through.
        return (
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            Missing required properties
                            <div className="btn-group float-end">
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target=".multi-collapse-pie" aria-expanded="false" aria-controls=""></i>
                            </div>
                        </div>
                        <div id={`collapseChart${props.uniqueIndex}`}
                             className="card-body collapse show multi-collapse-pie">
                            No chart options have been passed through - please check component properties and ensure valid
                            objects are getting passed through.
                        </div>
                    </div>
                </div>
        )
    }

    const getTopWaits = () => {
        const waits = props.chartOptions.find(option => option.key === 'Waits');
        if (!waits || !Array.isArray(waits.data)) return [];
        const sortedData = waits.data.sort((a, b) => b.y - a.y);
        // Calculate the total time
        const totalTime = sortedData.reduce((total, item) => total + item.y, 0);

        // Calculate 80% of the total time
        const eightyPercentTotal = 0.8 * totalTime;

        // Find the top items whose total time sums 80% of the total
        let cumulativeSum = 0;
        let topItems = [];
        for (let item of sortedData) {
            cumulativeSum += item.y;
            topItems.push(item);
            if (cumulativeSum >= eightyPercentTotal) {
                break;
            }
        }
        return topItems.map(item => item.name).slice(0, 8);
    }
    const topWaitsList = getTopWaits()

    return (
            <div className="col">
                <div className="card collapsible">
                    <div className="card-header d-flex align-items-center justify-content-between">
                        <div>
                            <i className="fal fa-chart-pie-alt fa-fw" aria-hidden="true"></i>
                            <span className="ms-1">{props.title ? props.title : `Top ${chartOption.key.toLowerCase()}`}</span>
                        </div>
                        <div>
                            <ConditionalRender if={props.instanceType && chartOption.key === 'Waits' && topWaitsList.length}>
                                <ChatButton
                                    value={Prompts.getInstanceWaitsPrompt(topWaitsList, props.instanceType)}
                                    label={isInStatementSidePanel ? 'Ask AI Co-pilot' : undefined}
                                />
                            </ConditionalRender>
                            <ConditionalRender if={!props.hideOptions}>
                                <button id="btnGroupDrop1" type="button"
                                        className="btn btn-xsm btn-primary dropdown-toggle ms-1" data-bs-toggle="dropdown"
                                        aria-expanded="false">
                                    {chartOption.key} Chart
                                </button>
                                <ul className="dropdown-menu border" aria-labelledby="btnGroupDrop1">
                                    {props.chartOptions.map((option: ChartOption, index) => (
                                            <li key={index}>
                                                {option.key === chartOption.key ? (
                                                        <span className="dropdown-item active" role="button"
                                                              onClick={() => setOption(option.key)}>{option.key}</span>
                                                ) : (
                                                        <span className="dropdown-item" role="button"
                                                              onClick={() => setOption(option.key)}>{option.key}</span>
                                                )}
                                            </li>
                                    ))}
                                </ul>
                            </ConditionalRender>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target=".multi-collapse-pie" aria-expanded="false" aria-controls=""></i>
                        </div>

                    </div>

                    <div id={`collapseChart${props.uniqueIndex}`} className="card-body collapse show multi-collapse-pie">
                        <HighchartsReact constructorType={"chart"} highcharts={Highcharts} options={chartOptions}/>
                    </div>

                </div>
            </div>
    );
}

export default PieChart;
